/**
 * Central de Ajuda — "Como fazer?". Um só sítio para o texto: a página /ajuda
 * do portal lê daqui, e o Manual do Utilizador (docs/manual) segue a mesma
 * ordem e os mesmos passos. Ao mudar um ecrã, actualizar aqui.
 *
 * Marcação mínima nos textos: **negrito** para nomes de menus e botões.
 */
export interface HelpArticle {
  slug: string;
  cat: HelpCategoryId;
  title: string;
  /** Uma frase: para que serve e quando usar. */
  intro: string;
  steps: string[];
  tip?: string;
  warn?: string;
  /** Palavras extra para a pesquisa (sinónimos que as pessoas escrevem). */
  keys?: string;
}

export type HelpCategoryId = 'comecar' | 'vender' | 'corrigir' | 'receber' | 'stock' | 'fiscal' | 'conta';

export const HELP_CATEGORIES: { id: HelpCategoryId; title: string; blurb: string }[] = [
  { id: 'comecar', title: 'Primeiros passos', blurb: 'Criar a conta, pôr os dados da empresa e chamar a equipa.' },
  { id: 'vender', title: 'Facturar e vender', blurb: 'Facturas, vendas a dinheiro, pró-formas e guias.' },
  { id: 'corrigir', title: 'Corrigir documentos', blurb: 'O que fazer quando uma factura saiu errada.' },
  { id: 'receber', title: 'Cobranças', blurb: 'Recibos, contas correntes e clientes em atraso.' },
  { id: 'stock', title: 'Stock e compras', blurb: 'Artigos, existências, fornecedores e inventários.' },
  { id: 'fiscal', title: 'AT e fiscal', blurb: 'Ficheiro mensal, SAF-T, QR e auditoria.' },
  { id: 'conta', title: 'Conta e segurança', blurb: 'Passwords, dupla verificação e subscrição.' },
];

export const HELP: HelpArticle[] = [
  // ── Primeiros passos ────────────────────────────────────────────
  {
    slug: 'criar-conta', cat: 'comecar', title: 'Criar a conta da empresa',
    intro: 'O registo leva uns dois minutos e não pede cartão. A empresa começa em modo formação, para experimentar à vontade.',
    steps: [
      'Em makila.sistemamz.com carregue em **Criar conta**.',
      'Escreva a firma, o NUIT, o regime de IVA e a morada da empresa. Depois os seus dados: nome, email e uma password com pelo menos 10 caracteres.',
      'Confirme o **endereço** da empresa. O sistema sugere um a partir do nome — por exemplo, *ferragens-tete* — e é por aí que a equipa entra: ferragens-tete.makila.sistemamz.com.',
      'Abra o email que lhe enviámos e carregue no link de confirmação.',
    ],
    tip: 'Guarde o endereço da empresa nos favoritos do browser e do telemóvel. É o caminho mais curto para entrar.',
    keys: 'registar registo inscrever conta nova começar',
  },
  {
    slug: 'entrar', cat: 'comecar', title: 'Entrar na aplicação da empresa',
    intro: 'Cada empresa tem o seu endereço próprio. Se não se lembra dele, a página Entrar do site encontra-o por si.',
    steps: [
      'Vá a makila.sistemamz.com e carregue em **Entrar**.',
      'Escreva o endereço da empresa (só a primeira parte, ex.: *ferragens-tete*) e carregue em **Continuar**.',
      'Na página da empresa, escreva o email e a password.',
      'Se activou a dupla verificação, introduza o código de 6 dígitos da aplicação autenticadora.',
    ],
    keys: 'login entrar aceder acesso endereço subdomínio',
  },
  {
    slug: 'dados-empresa', cat: 'comecar', title: 'Pôr os dados da empresa, o logótipo e as contas bancárias',
    intro: 'É o que sai no cabeçalho de cada factura. Vale a pena fazer isto antes do primeiro documento.',
    steps: [
      'Abra **Definições → Empresa**.',
      'Confirme a firma, o NUIT, o regime de IVA, a morada e os contactos. Se tiver, preencha o registo comercial (matrícula na Conservatória).',
      'Em **Logótipo**, carregue a imagem da empresa (PNG ou JPG, de preferência com fundo transparente).',
      'Em **Aspecto dos documentos**, escolha a cor principal, o rodapé e as condições gerais que quer impressas.',
      'Abra **Definições → Contas bancárias** e carregue em **Adicionar conta** para cada banco onde os clientes podem pagar. O NIB sai nas facturas.',
      'Carregue em **Guardar alterações**.',
    ],
    tip: 'Mudar a morada ou o logótipo não mexe nas facturas antigas: cada documento guarda os dados da empresa tal como estavam no dia em que foi emitido.',
    keys: 'configurar logo logotipo nuit morada banco nib cor rodapé',
  },
  {
    slug: 'equipa', cat: 'comecar', title: 'Convidar colegas e definir o que cada um pode fazer',
    intro: 'Cada pessoa entra com o seu email. O papel decide o que vê e o que pode fazer.',
    steps: [
      'Abra **Definições → Equipa** e carregue em **Convidar utilizador**.',
      'Escreva o nome e o email da pessoa e escolha o papel: **Administrador**, **Contabilista**, **Vendedor** ou **Consulta**.',
      'Carregue em **Convidar**. A pessoa recebe um email com um endereço (válido 7 dias) para definir a sua própria password — ninguém mais a conhece. Se já tiver conta Makila, é só avisada e passa a ver a empresa.',
    ],
    tip: 'O Vendedor factura e recebe, mas não mexe nas definições. A Consulta só vê. O Contabilista tem acesso aos relatórios e à comunicação AT. Só o Dono gere a subscrição.',
    keys: 'utilizador utilizadores convidar papel permissões colaborador funcionário',
  },
  {
    slug: 'modo-formacao', cat: 'comecar', title: 'Modo formação e emissão fiscal',
    intro: 'No modo formação pode emitir tudo o que quiser, mas os documentos saem marcados como "sem valor fiscal" e não contam para a AT.',
    steps: [
      'Use o modo formação para treinar a equipa e acertar os artigos, os clientes e o aspecto das facturas.',
      'Quando estiver pronto, abra **Definições → Empresa** e procure o cartão **Emissão fiscal**.',
      'Confirme o email e envie-nos a certidão do NUIT ou o registo comercial — confirmamos que a empresa é a titular do NUIT.',
      'Carregue em **Activar emissão fiscal**. A partir daí a numeração começa do 1 nas séries definitivas.',
    ],
    warn: 'A activação não se desfaz. Os documentos de formação ficam no sistema, mas nunca entram no ficheiro da AT.',
    keys: 'teste formação demonstração activar fiscal valor fiscal',
  },

  // ── Facturar e vender ───────────────────────────────────────────
  {
    slug: 'emitir-factura', cat: 'vender', title: 'Emitir uma factura',
    intro: 'A factura é para vendas a prazo: o cliente paga depois, e o valor fica na conta corrente dele.',
    steps: [
      'Carregue em **Novo** (canto superior) e escolha **Factura**, ou vá a **Vendas → Facturas** e carregue em **Nova factura**.',
      'Procure o cliente pelo nome ou NUIT. Se for novo, carregue em **Novo cliente** ali mesmo.',
      'Nas linhas, comece a escrever o nome do artigo: o preço e o IVA vêm do catálogo. Também pode escrever uma linha livre.',
      'Ajuste quantidades e descontos. O resumo à direita vai-se actualizando — base tributável, IVA por taxa e total.',
      'Confira o vencimento e carregue em **Emitir**. Confirme na janela que aparece.',
    ],
    tip: 'Não sabe ainda se vai emitir? Carregue em **Guardar rascunho**. Os rascunhos não gastam números e podem ser apagados.',
    warn: 'Depois de emitida, a factura não se edita nem se apaga — é a lei. Se houver um erro, corrige-se com uma nota de crédito.',
    keys: 'factura facturar vender venda nova emitir documento',
  },
  {
    slug: 'factura-recibo', cat: 'vender', title: 'Vender a pronto: factura-recibo ou venda a dinheiro',
    intro: 'Quando o cliente paga na hora não precisa de emitir factura e recibo separados.',
    steps: [
      'Escolha **Novo → Factura-recibo** (cliente com NUIT, normalmente uma empresa) ou **Venda a dinheiro** (consumidor final, ao balcão).',
      'Preencha as linhas como numa factura.',
      'Em **Pago com**, escolha o meio: numerário, M-Pesa, e-Mola, transferência, cartão ou cheque. Para M-Pesa e e-Mola, escreva o ID da transacção em **Referência**.',
      'Carregue em **Emitir**.',
    ],
    tip: 'No retalho, ligue **O preço inclui IVA** no artigo: o cliente paga exactamente o preço da etiqueta e o sistema calcula o IVA por dentro (CIVA art. 30).',
    keys: 'pronto pagamento balcão dinheiro vd fr mpesa emola numerário',
  },
  {
    slug: 'pro-forma', cat: 'vender', title: 'Fazer uma pró-forma e convertê-la em factura',
    intro: 'A pró-forma (cotação) não tem valor fiscal. Serve para o cliente aprovar antes de facturar.',
    steps: [
      'Escolha **Novo → Pró-forma**, preencha o cliente e as linhas e carregue em **Emitir**.',
      'Envie-a ao cliente por email, ou descarregue o **PDF**.',
      'Quando o cliente aprovar, abra a pró-forma, carregue em **⋯ (Mais acções)** e escolha **Converter em factura** (ou factura-recibo, ou venda a dinheiro).',
      'Confira e carregue em **Emitir**. A factura fica ligada à pró-forma de origem.',
    ],
    keys: 'cotação orçamento proforma pro-forma proposta converter',
  },
  {
    slug: 'avencas', cat: 'vender', title: 'Facturar todos os meses sem repetir trabalho (avenças)',
    intro: 'Mensalidades, contratos de manutenção ou rendas: o Makila gera o documento sozinho na data marcada.',
    steps: [
      'Faça (ou abra) a factura, factura-recibo ou pró-forma que se repete. Na descrição pode escrever **{mes}** — por exemplo "Avença de {mes}" sai "Avença de Novembro de 2026".',
      'Carregue em **⋯ → Repetir este documento (avença)**.',
      'Escolha a periodicidade (mensal, trimestral…), a data da próxima emissão e o que fazer nessa data: só preparar o rascunho, emitir, ou emitir e enviar por email.',
      'Acompanhe tudo em **Vendas → Avenças**: a próxima data, o último documento gerado, e os botões para gerar já, suspender ou apagar.',
    ],
    tip: 'Se alguma coisa impedir a emissão (cliente sem NUIT, limite do plano…), fica o rascunho para emitir à mão e recebe um email com o motivo.',
    keys: 'avença recorrente mensalidade repetir automática contrato renda subscrição',
  },
  {
    slug: 'seguimento-pro-formas', cat: 'vender', title: 'Acompanhar as pró-formas até virarem venda',
    intro: 'Uma pró-forma esquecida é uma venda perdida. O Makila pode lembrar o cliente por si.',
    steps: [
      'Em **Definições → Automação**, ligue **Acompanhar as pró-formas enviadas ao cliente** e escolha quantos dias depois do envio (por exemplo 3 e 7).',
      'A partir daí, cada pró-forma enviada por email recebe uma mensagem de seguimento nesses dias, e outra 2 dias antes de caducar.',
      'Quando a converte em factura, o seguimento pára sozinho.',
      'No **Painel**, o cartão **Para hoje** mostra as pró-formas que caducam nos próximos dias, com o telefone do cliente, e a taxa de conversão dos últimos 90 dias.',
    ],
    keys: 'pró-forma proforma cotação orçamento seguimento follow-up conversão lembrar cliente',
  },
  {
    slug: 'enviar-documento', cat: 'vender', title: 'Enviar uma factura ao cliente',
    intro: 'O cliente recebe o PDF em anexo e um link para confirmar que a factura é verdadeira.',
    steps: [
      'Abra o documento emitido e carregue em **Enviar**.',
      'O email do cliente vem preenchido da ficha. Pode pôr outras pessoas em **Cc**, separadas por **;**, e escrever uma mensagem.',
      'Carregue em **Enviar**. O envio fica registado no cartão **Envios** do documento.',
      'O email sai em nome da sua empresa, com a cor e o logótipo dela e a assinatura do **Departamento Comercial** com o seu nome. Para mudar o nome ou acrescentar o telemóvel: **Definições → Perfil e segurança**. As respostas do cliente seguem para o email da empresa.',
    ],
    tip: 'Para mandar pelo WhatsApp, descarregue o **PDF** e anexe-o na conversa. O documento electrónico tem o mesmo valor que o papel (Lei 3/2017).',
    keys: 'email enviar mandar whatsapp pdf partilhar imprimir assinatura cc copia perfil',
  },
  {
    slug: 'guia-remessa', cat: 'vender', title: 'Emitir uma guia de remessa',
    intro: 'A guia acompanha a mercadoria em trânsito. Pode facturá-la depois, de uma vez.',
    steps: [
      'Escolha **Novo → Guia de remessa**.',
      'Preencha o cliente e os artigos.',
      'No cartão **Transporte**, indique o local de carga, o local de descarga e a hora de início do transporte. A matrícula é opcional.',
      'Carregue em **Emitir** e imprima a guia para seguir com a carga.',
      'Mais tarde, abra a guia e escolha **⋯ → Facturar esta guia**.',
    ],
    keys: 'guia remessa transporte entrega carga mercadoria',
  },

  // ── Corrigir ────────────────────────────────────────────────────
  {
    slug: 'nota-credito', cat: 'corrigir', title: 'Corrigir ou anular uma factura (nota de crédito)',
    intro: 'Factura emitida não se apaga. Para a anular, ou para baixar o valor, emite-se uma nota de crédito que a referencia.',
    steps: [
      'Abra a factura (ou factura-recibo, ou venda a dinheiro) que quer corrigir.',
      'Carregue em **⋯ (Mais acções) → Emitir nota de crédito**.',
      'As linhas vêm copiadas da factura. Para anular tudo, deixe-as como estão. Para uma correcção parcial, ajuste quantidades ou preços.',
      'Escreva o **Motivo** — sai impresso. Por exemplo: "Devolução de 2 unidades" ou "Preço errado na linha 3".',
      'Se houve devolução física, ligue **Repor stock (devolução)**.',
      'Carregue em **Emitir**. O saldo do cliente baixa automaticamente.',
    ],
    tip: 'Se a factura tinha o preço baixo de mais, use **Emitir nota de débito** em vez da nota de crédito.',
    keys: 'anular cancelar corrigir erro errada nota de crédito nc devolução apagar factura',
  },
  {
    slug: 'rascunho', cat: 'corrigir', title: 'Editar ou apagar um rascunho',
    intro: 'Enquanto não for emitido, o documento é seu: pode mudar tudo ou deitá-lo fora.',
    steps: [
      'Em **Vendas**, os rascunhos aparecem com o contador ao lado de cada tipo.',
      'Abra o rascunho e carregue em **Editar** para mudar, ou em **⋯ → Apagar rascunho**.',
    ],
    keys: 'rascunho editar apagar alterar',
  },

  // ── Cobranças ───────────────────────────────────────────────────
  {
    slug: 'recibo', cat: 'receber', title: 'Registar um pagamento (recibo)',
    intro: 'O recibo liquida uma ou várias facturas do mesmo cliente, no todo ou em parte.',
    steps: [
      'Abra a ficha do cliente e carregue em **Recebimento** — ou, numa factura em aberto, **⋯ → Registar recebimento**.',
      'Aparecem as facturas pendentes. Escreva o valor recebido em cada uma, ou carregue em **Tudo**.',
      'Escolha como foi pago em **Recebido com** e escreva a referência (ID M-Pesa, n.º da transferência…).',
      'Carregue em **Emitir**.',
    ],
    tip: 'Um recibo emitido por engano pode ser anulado (com motivo). As facturas voltam a ficar pendentes.',
    keys: 'recibo pagamento receber recebimento cobrança liquidar pagar',
  },
  {
    slug: 'conta-corrente', cat: 'receber', title: 'Ver quem deve e mandar o extracto',
    intro: 'A conta corrente mostra o saldo de cada cliente e há quantos dias está cada factura em atraso.',
    steps: [
      'Abra **Clientes → Contas correntes** para ver os saldos por antiguidade (a vencer, 1–30, 31–60, 61–90, mais de 90 dias).',
      'Abra um cliente e escolha o separador **Extracto de conta**. Ajuste as datas.',
      'Carregue em **Extracto em PDF**, ou em **Aviso de facturas em aberto** para um lembrete mais curto.',
      'Para enviar logo, carregue em **Enviar por email**.',
    ],
    keys: 'conta corrente extracto saldo dívida devedores atraso aging lembrete',
  },
  {
    slug: 'limite-credito', cat: 'receber', title: 'Pôr um limite de crédito a um cliente',
    intro: 'Evita continuar a vender a prazo a quem já deve de mais.',
    steps: [
      'Abra o cliente, carregue em **⋯ → Editar** e vá ao separador **Condições comerciais**.',
      'Defina o **Limite de crédito (MT)**, os **Dias de atraso tolerados**, ou os dois.',
      'Guarde. Quando o limite for ultrapassado, o sistema avisa antes de emitir a factura. Só quem tem permissão pode avançar, e fica registado na auditoria.',
    ],
    keys: 'crédito limite plafond bloquear cliente',
  },

  {
    slug: 'cobranca-automatica', cat: 'receber', title: 'Lembrar os clientes de pagar, automaticamente',
    intro: 'O Makila envia lembretes de pagamento por si, antes e depois do vencimento, com a relação do que está em aberto em PDF.',
    steps: [
      'Em **Definições → Automação**, ligue **Enviar lembretes de pagamento**.',
      'Escolha quando: por exemplo **3 dias antes**, **No dia**, **7 dias depois** e **15 dias depois**.',
      'Os lembretes saem nos dias úteis às 9h, um email por cliente, para o email de facturação da ficha. A mesma etapa nunca se repete e uma factura paga deixa de ser lembrada.',
      'Os envios ficam no separador **Extracto de conta** do cliente, marcados como **Automático**.',
    ],
    tip: 'Para um cliente que não quer lembretes, edite a ficha e desligue **Lembretes automáticos**.',
    keys: 'cobrança automática lembrete pagamento atraso vencimento dívida email',
  },
  {
    slug: 'resumo-diario', cat: 'receber', title: 'Receber o resumo do dia por email',
    intro: 'Todas as manhãs, às 7h, o dono e os administradores recebem o que importa: vendas, recebimentos, o que está vencido, pró-formas a caducar e stock baixo.',
    steps: [
      'Em **Definições → Automação**, escolha em **Resumo por email** se o quer todos os dias, só à segunda-feira (resumo da semana) ou não o quer.',
      'Carregue em **Enviar-me o resumo agora** para ver como fica.',
      'Ligue também **Alerta de stock baixo** para receber um email logo que um artigo desce abaixo do mínimo, com a quantidade a encomendar.',
    ],
    keys: 'resumo diário email manhã relatório alerta stock baixo notificação',
  },

  // ── Stock e compras ─────────────────────────────────────────────
  {
    slug: 'criar-artigo', cat: 'stock', title: 'Criar um artigo ou serviço',
    intro: 'Com os artigos no catálogo, facturar é escrever três letras e escolher.',
    steps: [
      'Abra **Stock → Artigos** e carregue em **Novo artigo**.',
      'Escreva o código e a designação (é o texto que sai na factura) e escolha a unidade.',
      'Em preços, indique o preço de venda e a taxa de IVA: 16% normal, 5% reduzida ou isento. Se for isento, escolha o motivo — é obrigatório e sai impresso.',
      'Se for mercadoria, ligue **Controlar stock deste artigo** e defina o stock mínimo para receber alertas.',
      'Carregue em **Criar artigo**.',
    ],
    keys: 'artigo produto serviço catálogo preço iva isento código barras',
  },
  {
    slug: 'compra', cat: 'stock', title: 'Registar uma factura de fornecedor',
    intro: 'Dá entrada da mercadoria em stock, actualiza o custo médio e fica nas contas a pagar.',
    steps: [
      'Abra **Compras** e carregue em **Registar factura**.',
      'Escolha o fornecedor e escreva o número e a data da factura dele.',
      'Acrescente as linhas com quantidades e custos.',
      'Escolha o armazém de entrada e confirme. O stock sobe e o custo médio é recalculado.',
      'Quando pagar, registe o pagamento em **Compras → Pagamentos**.',
    ],
    keys: 'compra compras fornecedor factura fornecedor entrada stock pagar contas a pagar',
  },
  {
    slug: 'inventario', cat: 'stock', title: 'Fazer um inventário (contagem física)',
    intro: 'Conta o que está na prateleira e o sistema acerta o stock às quantidades reais.',
    steps: [
      'Abra **Stock → Inventários** e carregue em **Novo inventário**.',
      'Escolha o armazém e a data da contagem e carregue em **Abrir inventário**.',
      'Escreva as quantidades contadas. Pode ir carregando em **Guardar contagens** e continuar mais tarde.',
      'Veja a **Diferença valorizada** e, quando estiver tudo contado, carregue em **Fechar inventário**. Os acertos são lançados automaticamente.',
    ],
    keys: 'inventário contagem acerto existências quebra',
  },

  // ── AT e fiscal ─────────────────────────────────────────────────
  {
    slug: 'ficheiro-at', cat: 'fiscal', title: 'Gerar o ficheiro mensal para o e-Declaração',
    intro: 'Até ao último dia útil de cada mês entrega-se à AT a lista das facturas do mês anterior (Aviso n.º 40/AT/DGI/2025).',
    steps: [
      'Abra **Fiscal → Comunicação AT** e escolha o mês.',
      'Confira o número de documentos, a base tributável e o IVA.',
      'Carregue em **CSV**, **Excel** ou **XML** — os três formatos que o e-Declaração aceita.',
      'Carregue o ficheiro no portal e-Declaração da AT.',
      'Volte ao Makila e carregue em **Marcar submetido** no histórico, para ficar registado.',
    ],
    tip: 'Dez dias antes do prazo, o sistema começa a lembrá-lo por email enquanto o mês anterior não estiver marcado como submetido.',
    keys: 'at autoridade tributária edeclaração e-declaração ficheiro mensal comunicação csv xml excel prazo',
  },
  {
    slug: 'saft', cat: 'fiscal', title: 'Exportar o SAF-T (MZ)',
    intro: 'O SAF-T é o ficheiro normalizado que a AT está a adoptar. Também serve para o contabilista ou para uma inspecção.',
    steps: [
      'Abra **Fiscal → Comunicação AT** e escolha o período.',
      'Carregue em **SAF-T (MZ)**. O ficheiro fica no histórico, com a sua impressão digital (SHA-256).',
    ],
    keys: 'saft saf-t xml contabilista inspecção exportar',
  },
  {
    slug: 'verificar-qr', cat: 'fiscal', title: 'Verificar uma factura pelo código QR',
    intro: 'Qualquer pessoa pode confirmar, com a câmara do telemóvel, que uma factura Makila é verdadeira.',
    steps: [
      'Aponte a câmara ao código QR impresso no documento.',
      'Abra o link. A página mostra o emitente, o número, a data e o total registados no sistema.',
      'Compare com o papel. Se algum valor não bater certo, o documento foi alterado depois de emitido.',
    ],
    keys: 'qr código verificar autenticidade falso verdadeiro',
  },
  {
    slug: 'auditoria', cat: 'fiscal', title: 'Ver quem fez o quê (auditoria)',
    intro: 'Todas as acções importantes ficam registadas e não podem ser apagadas — nem por nós.',
    steps: [
      'Abra **Fiscal → Auditoria**.',
      'Filtre por tipo de acção ou datas. Vê quem foi, quando, de que endereço IP, e o que mudou.',
    ],
    keys: 'auditoria registo histórico log quem alterou',
  },

  // ── Conta e segurança ───────────────────────────────────────────
  {
    slug: 'password', cat: 'conta', title: 'Recuperar ou mudar a password',
    intro: 'Esqueceu-se? Recebe um link por email, válido durante uma hora.',
    steps: [
      'Na página de entrada da empresa, carregue em **Esqueci-me da password** e escreva o seu email.',
      'Abra o email e carregue no link. Escolha uma password nova com pelo menos 10 caracteres.',
      'Para mudar a password estando dentro do sistema, vá a **Definições → Segurança**.',
    ],
    tip: 'Ao mudar a password, as sessões abertas noutros computadores e telemóveis são terminadas.',
    keys: 'password senha esqueci recuperar mudar alterar palavra-passe',
  },
  {
    slug: 'dupla-verificacao', cat: 'conta', title: 'Activar a dupla verificação (2FA)',
    intro: 'Mesmo que alguém descubra a sua password, não entra sem o código do seu telemóvel.',
    steps: [
      'Instale no telemóvel uma aplicação autenticadora (Google Authenticator, Microsoft Authenticator, Aegis…).',
      'Vá a **Definições → Segurança** e carregue em **Activar**.',
      'Na aplicação, acrescente uma conta e escreva a chave que o Makila mostra. Depois introduza o código de 6 dígitos que aparece.',
      'Guarde os códigos de recuperação num sítio seguro — só aparecem uma vez. Servem para entrar se perder o telemóvel.',
    ],
    keys: '2fa dupla verificação autenticação dois factores segurança totp authenticator',
  },
  {
    slug: 'subscricao', cat: 'conta', title: 'Renovar ou mudar de plano',
    intro: 'A subscrição gere-se dentro da aplicação. Só o Dono da conta vê esta área.',
    steps: [
      'Abra **Definições → Subscrição e pagamento**.',
      'Escolha o plano e a duração. No pagamento anual paga 10 meses e usa 12.',
      'Escolha como quer pagar: M-Pesa, e-Mola, cartão ou transferência bancária.',
      'Siga as instruções. Na transferência, carregue o comprovativo — activamos depois de confirmar o crédito.',
      'Quando o pagamento é confirmado, a subscrição fica activa logo e recebe a confirmação por email.',
    ],
    tip: 'Se a subscrição expirar, a conta fica só de leitura: continua a ver e exportar tudo, só não emite documentos novos. Nada é apagado.',
    keys: 'subscrição plano pagar renovar renovação mpesa preço upgrade',
  },
];

/** Pesquisa simples sem acentos, em título, introdução, passos e palavras-chave. */
export function searchHelp(q: string): HelpArticle[] {
  const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const terms = norm(q).split(/\s+/).filter((t) => t.length > 1);
  if (!terms.length) return HELP;
  return HELP
    .map((a) => {
      const title = norm(a.title);
      const body = norm([a.intro, a.keys ?? '', ...a.steps].join(' '));
      const score = terms.reduce((s, t) => s + (title.includes(t) ? 3 : 0) + (body.includes(t) ? 1 : 0), 0);
      const all = terms.every((t) => title.includes(t) || body.includes(t));
      return { a, score: all ? score : 0 };
    })
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score)
    .map((x) => x.a);
}
